class MonthlyReportingGridController {

    constructor(
        $state,
        $stateParams,
        appConfig,
        moment
    ) {
        'ngInject';

        this.$state = $state;
        this.$stateParams = $stateParams;
        this.appConfig = appConfig;
        this.moment = moment;
    }

    $onInit() {
        this.canRunReport = false;
        this.selectedMonthIndices = [];
        this.sortIndex = 0;
        this.sortProp = null;
        this.sortPropDisplay = null;
        this.clientIds = this.$stateParams.clientIds;
        this.year = parseInt(this.$stateParams.year);
        this.monthColumns = this.initializeMonthColumns();
        this.sortColumns = this.columns.filter((column) => column.showSort);

        this.baseFilters = this.initializeFilters(this.columns);
        this.search = angular.copy(this.baseFilters);

        const rollupColumn = [
            {
                text: '',
                columnSize: 'sm',
                showSort: false
            },
        ];

        this.headers = [...rollupColumn, ...this.columns, ...this.monthColumns];
    }

    /***
     * Generates a list of objects that are used for a few things, including:
     * - displaying the list of months and their checkboxes
     * - tracking which month checkboxes have been selected
     * - tracking the month index to be used when requesting a report (e.g. Jan = 1, Feb = 2)
     *
     * @returns a list of objects
     */
    initializeMonthColumns() {
        return [...Array(12).keys()].map((item) => {
           return {
               text: this.moment().month(item).format('MMM'),
               index: item + 1,
               columnSize: 'md',
               isSelected: false,
               showSort: false
           }
        });
    }

    /***
     * Deselect all of the selected rows and months that have been selected for the aggregate report.
     */
    deselectAllItems() {
        this.selectedRows().map((reportRow) => reportRow.isSelected = false);
        this.deselectAllMonths();
        this.canRunReport = false;
    }

    /***
     * Deselect all of the selected months that have been selected for the aggregate report.
     */
    deselectAllMonths() {
        this.monthColumns.map((monthColumn) => monthColumn.isSelected = false);
        this.selectedMonthIndices = [];
    }

    /***
     * Create an initial empty filters object that will be used to track the filters for each of the columns.
     *
     * @param columns   a list of objects, where each object is expected to contain a nestedDisplayKeys array which
     *                  contains the keys that boil down to the field's filterable value. e.g. ['client', 'name']
     *                  indicates an object with the format {client: {name: "client name"}}
     *
     * @returns an object matching the nestedDisplayKeys hierarchy of each of the filterable columns
     */
    initializeFilters(columns) {
        let search = {};
        columns.map((column) => {
            let currentColumnFilters = {};
            column.nestedDisplayKeys.reduce((accumulator, key, index) => {
                let nestedValue = index+1 >= column.nestedDisplayKeys.length ? undefined : {};
                return accumulator[key] = nestedValue;
            }, currentColumnFilters);
            Object.assign(search, currentColumnFilters);
        });
        return search;
    }

    /***
     * Changes the current year of reporting values that the user is viewing.
     *
     * @param year  the year to navigate to.
     */
    goToYear(year) {
        this.isYearLoadingLeft = year < this.year;
        this.isYearLoadingRight = year > this.year;

        this.$state.go(this.$state.current , {
            clientIds: this.clientIds,
            year: year
        },
            {
                reload: true
            });
    }

    /***
     * A helper function to return the subset of months that have been selected.
     *
     * @returns list of objects
     */
    selectedMonths() {
        return this.monthColumns.filter((m) => m.isSelected);
    }

    /***
     * A helper function to return the subset of facility rows that have been selected.
     *
     * @returns list of objects
     */
    selectedRows() {
        return this.reportList.filter((report) => report.isSelected);
    }

    /***
     * Resets the column filters.
     */
    resetFilters() {
        this.search = angular.copy(this.baseFilters);
    }

    /***
     * Sort the list of reports based on the given column.
     * Iterates through 3 sort states, tracked by this.sortIndex:
     * 0 - no sort
     * 1 - sort ascending
     * 2 - sort descending
     *
     * @param sortColumnName    the column to sort the table by
     */
    sortList(sortColumnName) {
        const sortColumn = this.sortColumns.filter((column) => column.text === sortColumnName)[0];
        if (sortColumn === undefined) {
            return;
        }
        if (this.sortPropDisplay !== sortColumnName) {
            this.sortIndex = 0;
        }
        this.sortPropDisplay = sortColumnName;
        this.sortIndex = (this.sortIndex + 1) % 3;
        this.isReverseSort = this.sortIndex === 2;
        this.sortProp = this.sortIndex === 0 ? null : sortColumn.nestedDisplayKeys.join('.');
    }

    /***
     *  Toggles the list of rows that are selected for the aggregate report. If there is already one or more rows
     *  selected from a different client than the one being toggled and we are restricting rollups to a single client,
     *  an error message is displayed instead.
     *
     * @param $event        the ng-click event
     * @param reportRow     the full report object that is being selected.
     */
    toggleRow($event, reportRow) {
        if (this.selectedMonths().length > 1 && !reportRow.isSelected && this.selectedRows().length > 0) {
            $event.preventDefault();
            alert("Multi-month roll-up reports can only be run with a single Facility. Please go back and only " +
                  "select a single facility. If you are trying to run a multi-facility report, please go back and " +
                  "only select a single month.");
            return;
        }
        if (this.restrictRollupToOneClient) {
            const rowsFromOtherClients = this.selectedRows().filter((row) => row.client.id !== reportRow.client.id);
            if (rowsFromOtherClients.length > 0) {
                $event.preventDefault();
                alert("Roll-up reports can only be run within a single client. Please go back and only " +
                      "select facilities from one client.");
                return;
            }
        }
        reportRow.isSelected = !reportRow.isSelected;

        this.updateRollupUrl();
    }

    /***
     *  Toggle a month selection. If the month was previously selected, deselect it. Otherwise, replace the previous
     *  selection with the new one (only a single month can be selected at once).
     *
     * @param $event      the ng-click event
     * @param month       the month object that was selected, as defined by this.monthColumns
     */
    toggleMonthSelection($event, month) {
        const selectedMonths = this.selectedMonths();

        // Enables multi-month reporting in JasperSoft
        if (selectedMonths.length > 0 && this.selectedRows().length > 1) {
            $event.preventDefault();
            alert("Multi-month roll-up reports can only be run with a single Facility. Please go back and only " +
                  "select a single facility.");
            return;
        }

        if (!this.allowMultipleMonthSelection) {
            this.deselectAllMonths()
        }

        month.isSelected = !month.isSelected;
        this.selectedMonthIndices = this.monthColumns.filter((m) => m.isSelected).map((m) => m.index);

        this.updateRollupUrl();
    }

    /***
     * Update the rollup report URL with the latest month and row selections.
     */
    updateRollupUrl() {
        this.onUpdateRollupUrl({
            selectedRows: this.selectedRows(),
            months: this.selectedMonths().map((month) => month.index),
            year: this.year
        });
        this.canRunReport = this.selectedRows().length > 0 && this.selectedMonths().length > 0;
    }
}

export default MonthlyReportingGridController;


// WEBPACK FOOTER //
// ./app/common/monthly-reporting/monthly-reporting-grid/monthly-reporting-grid-controller.js